import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ExtractedContactData {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  job_title?: string;
  department?: string;
  linkedin_url?: string;
  notes?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  confidence: number;
}

export interface BusinessCardScanResult {
  extractedData: ExtractedContactData;
  imagePath: string;
  imageUrl: string;
  rawExtraction?: any;
}

export function useBusinessCardScanner() {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const scanBusinessCard = async (file: File): Promise<BusinessCardScanResult> => {
    setIsScanning(true);
    setError(null);

    try {
      // Step 1: Upload image to storage
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Sanitize filename
      const sanitizedName = file.name
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/_+/g, '_');
      
      const timestamp = Date.now();
      const filePath = `${user.id}/${timestamp}-${sanitizedName}`;

      console.log('Uploading business card image...');
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('business-cards')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Get signed URL (bucket is private)
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('business-cards')
        .createSignedUrl(filePath, 604800); // 7 days expiry

      if (signedUrlError) throw signedUrlError;
      if (!signedUrlData?.signedUrl) throw new Error('Failed to generate signed URL');

      // Step 2: Convert to base64 for AI processing
      const base64Data = await fileToBase64(file);

      // Step 3: Call AI extraction edge function
      console.log('Extracting contact information...');
      const { data: extractionData, error: extractionError } = await supabase.functions.invoke(
        'extract-business-card',
        {
          body: { imageData: base64Data }
        }
      );

      if (extractionError) throw extractionError;
      if (!extractionData.success) {
        throw new Error(extractionData.error || 'Extraction failed');
      }

      console.log('Extraction successful:', extractionData.data);

      toast({
        title: "Business card scanned",
        description: `Contact information extracted with ${Math.round(extractionData.data.confidence * 100)}% confidence`,
      });

      return {
        extractedData: extractionData.data,
        imagePath: uploadData.path,
        imageUrl: signedUrlData.signedUrl,
        rawExtraction: extractionData.rawExtraction,
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to scan business card';
      setError(errorMessage);
      console.error('Business card scan error:', err);
      
      toast({
        title: "Scan failed",
        description: errorMessage,
        variant: "destructive",
      });

      throw err;
    } finally {
      setIsScanning(false);
    }
  };

  const clearError = () => setError(null);

  return {
    scanBusinessCard,
    isScanning,
    error,
    clearError,
  };
}

// Helper function to convert File to base64
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}
